import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, Keyboard, Plus, Search, Edit2 } from 'lucide-react';
import api from '../utils/api';
import { CLIENT_SERVICES } from '../utils/helpers';
import toast from 'react-hot-toast';

export const SHORTCUTS_KEY = 'ku_shortcuts_v1';

export const ALL_SHORTCUTS = [
  // Navigation
  { id: 'nav_1', category: 'Navigation', label: 'Dashboard',   defaultKey: '1', type: 'nav',    target: '/' },
  { id: 'nav_2', category: 'Navigation', label: 'Clients',     defaultKey: '2', type: 'nav',    target: '/clients' },
  { id: 'nav_3', category: 'Navigation', label: 'Income Tax',  defaultKey: '3', type: 'nav',    target: '/income-tax' },
  { id: 'nav_4', category: 'Navigation', label: 'TDS',         defaultKey: '4', type: 'nav',    target: '/tds' },
  { id: 'nav_5', category: 'Navigation', label: 'GST',         defaultKey: '5', type: 'nav',    target: '/gst' },
  { id: 'nav_6', category: 'Navigation', label: 'Audit',       defaultKey: '6', type: 'nav',    target: '/audit' },
  { id: 'nav_7', category: 'Navigation', label: 'Accounts',    defaultKey: '7', type: 'nav',    target: '/accounts' },
  { id: 'nav_8', category: 'Navigation', label: 'ROC',         defaultKey: '8', type: 'nav',    target: '/roc' },
  { id: 'nav_9', category: 'Navigation', label: 'ESI/PF',      defaultKey: '9', type: 'nav',    target: '/esipf' },
  { id: 'nav_0', category: 'Navigation', label: 'Tasks',       defaultKey: '0', type: 'nav',    target: '/tasks' },
  // Modules
  { id: 'mod_m', category: 'Modules', label: 'Meetings',    defaultKey: 'm', type: 'nav',    target: '/meetings' },
  { id: 'mod_k', category: 'Modules', label: 'Checklists',  defaultKey: 'k', type: 'nav',    target: '/checklists' },
  { id: 'mod_b', category: 'Modules', label: 'Billing',     defaultKey: 'b', type: 'nav',    target: '/billing' },
  { id: 'mod_d', category: 'Modules', label: 'Documents',   defaultKey: 'd', type: 'nav',    target: '/documents' },
  { id: 'mod_r', category: 'Modules', label: 'Reports',     defaultKey: 'r', type: 'nav',    target: '/reports' },
  { id: 'mod_e', category: 'Modules', label: 'Employees',   defaultKey: 'e', type: 'nav',    target: '/employees' },
  { id: 'mod_s', category: 'Modules', label: 'Settings',    defaultKey: 's', type: 'nav',    target: '/settings' },
  // Actions
  { id: 'act_c',    category: 'Actions', label: 'Quick Add Client',    defaultKey: 'c', type: 'action', target: 'quickadd' },
  { id: 'act_a',    category: 'Actions', label: 'Quick Edit Client',   defaultKey: 'a', type: 'action', target: 'quickedit' },
  { id: 'act_n',    category: 'Actions', label: 'New Meeting',         defaultKey: 'n', type: 'nav',    target: '/meetings' },
  { id: 'act_t',    category: 'Actions', label: 'Tasks page',          defaultKey: 't', type: 'nav',    target: '/tasks' },
  { id: 'act_i',    category: 'Actions', label: 'New record (entry row / add form)', defaultKey: 'i', type: 'action', target: 'newrecord' },
  { id: 'act_help', category: 'Actions', label: 'Show shortcut help',  defaultKey: '?', type: 'action', target: 'help' },
];

export function getActiveShortcuts() {
  try {
    const overrides = JSON.parse(localStorage.getItem(SHORTCUTS_KEY) || '{}');
    return ALL_SHORTCUTS.map(s => ({
      ...s,
      key: overrides[s.id] !== undefined ? overrides[s.id] : s.defaultKey.toLowerCase(),
    }));
  } catch {
    return ALL_SHORTCUTS.map(s => ({ ...s, key: s.defaultKey.toLowerCase() }));
  }
}

export function saveShortcutOverride(id, key) {
  try {
    const overrides = JSON.parse(localStorage.getItem(SHORTCUTS_KEY) || '{}');
    overrides[id] = key.toLowerCase();
    localStorage.setItem(SHORTCUTS_KEY, JSON.stringify(overrides));
  } catch {}
}

export function resetShortcuts() {
  localStorage.removeItem(SHORTCUTS_KEY);
}

const ENTITY_TYPES = ['Individual', 'HUF', 'Firm', 'LLP', 'Private Limited', 'Public Limited', 'Trust', 'AOP', 'BOI', 'Other'];
const SERVICES = CLIENT_SERVICES;

function HelpModal({ onClose }) {
  const active = getActiveShortcuts();
  const byCategory = ['Navigation', 'Modules', 'Actions'].map(cat => ({
    cat,
    shortcuts: active.filter(s => s.category === cat),
  }));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-6 py-4 border-b bg-[#1e3a5f] rounded-t-2xl">
          <Keyboard size={18} className="text-[#f0a500]" />
          <h3 className="text-white font-semibold">Keyboard Shortcuts</h3>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5 text-sm">
          {byCategory.map(({ cat, shortcuts }) => (
            <div key={cat}>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{cat} (Alt + key)</p>
              <div className="grid grid-cols-2 gap-1">
                {shortcuts.map(s => (
                  <div key={s.id} className="flex items-center gap-2 py-1">
                    <kbd className="bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5 text-xs font-mono min-w-[52px] text-center">
                      Alt+{s.key.toUpperCase()}
                    </kbd>
                    <span className="text-gray-600">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="pt-2 border-t border-gray-100 text-center">
            <Link to="/settings" onClick={onClose} className="text-xs text-[#1e3a5f] hover:text-[#f0a500] font-medium flex items-center justify-center gap-1">
              <Keyboard size={12} /> Customize shortcuts →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAddClientModal({ onClose }) {
  const [form, setForm] = useState({ name: '', client_code: '', entity_type: 'Individual', mobile: '', services: [] });
  const [saving, setSaving] = useState(false);
  const nameRef = useRef();
  useEffect(() => { nameRef.current?.focus(); }, []);

  const toggleService = s => setForm(f => ({
    ...f,
    services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s],
  }));

  const handleSave = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    // Server requires client_code — without this check the quick-add always failed with a 400
    if (!form.client_code.trim()) { toast.error('Client Code is required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/clients', {
        name: form.name.trim(),
        client_code: form.client_code.trim(),
        entity_type: form.entity_type,
        mobile: form.mobile.trim() || undefined,
        services: form.services, // array — server JSON-encodes it
        status: 'Active',
      });
      toast.success(`Client "${form.name}" created`);
      // Let open module pages refresh their cached client lists (entry-row autocomplete)
      window.dispatchEvent(new CustomEvent('pms:clients-changed', { detail: { id: r.data?.id, name: form.name.trim() } }));
      onClose();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed to create client'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-6 py-4 border-b bg-[#1e3a5f] rounded-t-2xl">
          <Plus size={18} className="text-[#f0a500]" />
          <h3 className="text-white font-semibold">Quick Add Client</h3>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Client Name *</label>
              <input ref={nameRef} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="input-field" placeholder="e.g. Ravi Kumar" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client Code *</label>
              <input value={form.client_code} onChange={e => setForm(f => ({ ...f, client_code: e.target.value }))}
                className="input-field" placeholder="e.g. KUA-C001" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
              <input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                className="input-field" placeholder="9876543210" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Entity Type *</label>
              <select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} className="input-field">
                {ENTITY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Services</label>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map(s => (
                <button key={s} type="button" onClick={() => toggleService(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${form.services.includes(s) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e3a5f]'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 btn-primary py-2.5">
              {saving ? 'Creating…' : 'Create Client'}
            </button>
            <button type="button" onClick={onClose} className="flex-1 btn-secondary py-2.5">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ENTITY_TYPES_EDIT = ['Individual', 'HUF', 'Firm', 'LLP', 'Pvt Ltd', 'Public Ltd', 'Trust', 'AOP', 'Other'];
const SERVICES_EDIT = CLIENT_SERVICES;

function ClientSearchModal({ onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/clients', { params: { search: query } })
        .then(r => setResults(r.data.slice(0, 8)))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[210] pt-20 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-[#1e3a5f] rounded-t-2xl">
          <Search size={16} className="text-[#f0a500]" />
          <span className="text-white font-semibold text-sm">Quick Edit Client (Alt+A)</span>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-3">
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or client code…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]" />
        </div>
        {loading && <div className="px-4 pb-3 text-xs text-gray-400">Searching…</div>}
        {results.length > 0 && (
          <div className="border-t divide-y max-h-64 overflow-y-auto">
            {results.map(c => (
              <button key={c.id} onClick={() => onSelect(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center gap-3 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-800 truncate">{c.name}</div>
                  <div className="text-xs text-gray-400">{c.client_code} · {c.entity_type} · {c.status}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        {!loading && query.trim() && results.length === 0 && (
          <div className="px-4 pb-3 text-xs text-gray-400">No clients found</div>
        )}
      </div>
    </div>
  );
}

function QuickEditClientModal({ client: initialClient, onClose }) {
  const [form, setForm] = useState({ ...initialClient, services: initialClient.services || [] });
  const [staff, setStaff] = useState([]);
  const [referringCAs, setReferringCAs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {});
    api.get('/referring-cas').then(r => setReferringCAs(r.data)).catch(() => {});
  }, []);

  const toggleSvc = s => setForm(f => ({
    ...f, services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s],
  }));

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${initialClient.id}`, { ...form, services: form.services });
      toast.success('Client updated successfully');
      onClose();
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[210] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-6 py-4 border-b bg-[#1e3a5f] rounded-t-2xl sticky top-0">
          <Edit2 size={16} className="text-[#f0a500]" />
          <h3 className="text-white font-semibold">Edit Client — {initialClient.name}</h3>
          {initialClient.client_code && <span className="ml-1 text-xs bg-white/20 text-white px-2 py-0.5 rounded font-mono">{initialClient.client_code}</span>}
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Client Code <span className="text-gray-300">(read-only)</span></label>
              <input value={form.client_code || ''} readOnly className="input-field mt-1 bg-gray-50 font-mono text-gray-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Client Name *</label>
              <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="input-field mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Entity Type</label>
              <select value={form.entity_type || ''} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))} className="input-field mt-1">
                {ENTITY_TYPES_EDIT.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Status</label>
              <select value={form.status || 'Active'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field mt-1">
                {['Active','Inactive','Prospect'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">PAN</label>
              <input value={form.pan || ''} onChange={e => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} className="input-field mt-1 font-mono" maxLength={10} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">TAN</label>
              <input value={form.tan || ''} onChange={e => setForm(f => ({ ...f, tan: e.target.value.toUpperCase() }))} className="input-field mt-1 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">GSTIN</label>
              <input value={form.gstin || ''} onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} className="input-field mt-1 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Mobile</label>
              <input value={form.mobile || ''} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} className="input-field mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">WhatsApp</label>
              <input value={form.whatsapp || ''} onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} className="input-field mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Email</label>
              <input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="input-field mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Assigned Staff</label>
              <select value={form.assigned_staff_id || ''} onChange={e => setForm(f => ({ ...f, assigned_staff_id: e.target.value }))} className="input-field mt-1">
                <option value="">— Select —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Billed via (Referring CA)</label>
              <select value={form.referring_ca_id || ''} onChange={e => setForm(f => ({ ...f, referring_ca_id: e.target.value ? parseInt(e.target.value) : null }))} className="input-field mt-1">
                <option value="">Direct billing (default)</option>
                {referringCAs.map(rc => <option key={rc.id} value={rc.id}>{rc.name}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-500">Address</label>
              <textarea value={form.address_registered || ''} onChange={e => setForm(f => ({ ...f, address_registered: e.target.value }))} className="input-field mt-1" rows={2} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-2 block">Services</label>
            <div className="flex flex-wrap gap-2">
              {SERVICES_EDIT.map(s => (
                <button key={s} type="button" onClick={() => toggleSvc(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${form.services.includes(s) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#1e3a5f]'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 btn-primary py-2.5">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" onClick={onClose} className="flex-1 btn-secondary py-2.5">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function GlobalShortcuts() {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [editClient, setEditClient] = useState(null);

  useEffect(() => {
    const handler = e => {
      if (!e.altKey) return;
      const tag = document.activeElement?.tagName;
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const key = e.key.toLowerCase();

      // Help always fires regardless of input focus
      if (key === '?' || e.key === '?') {
        e.preventDefault();
        setShowHelp(h => !h);
        return;
      }

      if (inInput) return;

      const active = getActiveShortcuts();
      const match = active.find(s => s.key === key);
      if (!match) return;

      e.preventDefault();
      if (match.type === 'nav') {
        navigate(match.target);
      } else if (match.type === 'action') {
        if (match.target === 'quickadd') setShowQuickAdd(true);
        else if (match.target === 'quickedit') setShowClientSearch(true);
        else if (match.target === 'help') setShowHelp(h => !h);
        else if (match.target === 'newrecord') {
          // Each module page listens and focuses its entry row / opens its Add form
          window.dispatchEvent(new CustomEvent('pms:new-record'));
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return (
    <>
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showQuickAdd && <QuickAddClientModal onClose={() => setShowQuickAdd(false)} />}
      {showClientSearch && !editClient && (
        <ClientSearchModal
          onSelect={c => { setEditClient(c); setShowClientSearch(false); }}
          onClose={() => setShowClientSearch(false)}
        />
      )}
      {editClient && (
        <QuickEditClientModal
          client={editClient}
          onClose={() => setEditClient(null)}
        />
      )}
    </>
  );
}
